import './index.scss'
import React, { useRef, useEffect, useState } from 'react';
import { Card, Form, Input, Button, Typography, Spin, message } from 'antd'
import logo from '@/assets/AspectLogo.svg'
import { useDispatch } from 'react-redux'
import { fetchLogin, fetchUserInfo } from '@/store/modules/user'
import { useNavigate } from 'react-router-dom'
import LogUserActivity from '@/components/LogUserActivity';

const { Paragraph, Text } = Typography

// 🔹 配置 message 全局居中
message.config({
  top: '40%',          // 垂直居中，可微调
  duration: 2,
  maxCount: 3,          // 同时最多显示 3 条
  rtl: false,
})

const Login = () => {
  const inputRef = useRef(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [bidInfo, setBidInfo] = useState(null)
  const [loadingBid, setLoadingBid] = useState(true)

  // ✅ 获取当前 Bid
  useEffect(() => {
    const fetchBid = async () => {
      try {
        const res = await fetch('https://dc28.job-bid.ca/server/api/current.php')
        const text = await res.text()
        let data = null
        try { data = JSON.parse(text) } 
        catch (err) { console.error('JSON Parse Error:', err); setBidInfo(null); return }
        if (data && data.success) setBidInfo(data.data)
        else setBidInfo(null)
      } catch (e) { console.error('Fetch Bid Error:', e); setBidInfo(null) }
      finally { setLoadingBid(false) }
    }
    fetchBid()
  }, [])

  const getBidStatus = (bid) => {
    if (!bid) return { status: 'NONE' }
    const now = new Date()
    const start = new Date(bid.startDate.replace(' ', 'T'))
    const end = new Date(bid.endDate.replace(' ', 'T'))
    if (now < start) return { status: 'UPCOMING', days: Math.ceil((start - now) / (1000*60*60*24)) }
    if (now >= start && now <= end) return { status: 'OPEN', days: Math.ceil((end - now) / (1000*60*60*24)) }
    return { status: 'CLOSED' }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return new Date(dateStr.replace(' ', 'T')).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' })
  }

  const getStatusLabel = (status) => {
    switch(status) {
      case 'UPCOMING': return 'Days Until Start'
      case 'OPEN': return 'Days Left'
      default: return ''
    }
  }
  
  const [loadingLogin, setLoadingLogin] = useState(false)  // 🔹 登录加载状态

  const onFinish = async (values) => {
    try {
      setLoadingLogin(true)  // 🔹 开始加载
      await dispatch(fetchLogin(values))
      const user = await dispatch(fetchUserInfo())

      if (user && user.role) {
        message.success({ content: 'Login successful', duration: 2 })
        if (user.role === 'Admin') navigate('/', { replace: true })
        else navigate('/Jobbid', { replace: true })
      } else {
        message.error({ content: 'User role not found', duration: 2 })
      }
    } catch (e) {
      console.error('Login Error:', e)
      message.error({ content: 'Invalid credentials or network error', duration: 2 })
      setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select() }, 2000)
    } finally {
      setLoadingLogin(false)  // 🔹 结束加载
    }
  }

  const bidStatus = getBidStatus(bidInfo)

  return (
    <div className="login">
      <Card className="login-container">

        <img className="login-logo" src={logo} alt="Logo" />
        <LogUserActivity category="client" />

        <Form onFinish={onFinish} validateTrigger="onBlur">

          {/* Employee ID */}
          <Form.Item
            name="employeeID"
            rules={[
              { required: true, message: 'Please enter your login ID' },
              { min: 5, message: 'Employee ID must be at least 5 characters' },
              { pattern: /^[a-zA-Z0-9]+$/, message: 'Letters and numbers only' }
            ]}
          >
            <Input ref={inputRef} size="large" placeholder="Employee ID" />
          </Form.Item>

          {/* Password */}
          <Form.Item
            name="code"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password size="large" placeholder="Password" />
          </Form.Item>

          {/* Login Button */}
          <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={loadingLogin} // 🔹 按钮显示 loading
            disabled={loadingLogin} // 🔹 禁用防止重复点击
          >
            {loadingLogin ? 'Logging in...' : 'Login'}
          </Button>
        </Form.Item>

          {/* BID Info */}
          <div className="login-info">
            <Paragraph><Text strong>BID STATUS</Text></Paragraph>

            {loadingBid ? (
              <Spin tip="Loading bid..." />
            ) : !bidInfo ? (
              <Paragraph><Text type="secondary">No active bid</Text></Paragraph>
            ) : (
              <div className="bid-details">
                <Paragraph className="bid-name"><Text strong>{bidInfo.name}</Text></Paragraph>
                <Paragraph><Text strong>Opens:</Text> {formatDate(bidInfo.startDate)}</Paragraph>
                <Paragraph><Text strong>Ends:</Text> {formatDate(bidInfo.endDate)}</Paragraph>
                <Paragraph>Status: <Text className={`status-${bidStatus.status.toLowerCase()}`}>{bidStatus.status}</Text></Paragraph>
                {bidStatus.days && (
                  <Paragraph className="bid-days">
                    <Text strong><span className="highlight">{bidStatus.days}</span> {getStatusLabel(bidStatus.status)}</Text>
                  </Paragraph>
                )}
              </div>
            )}

            <Paragraph className="help-text">Need Help? Please see your Senior Supervisor.</Paragraph>
          </div>

        </Form>
      </Card>
    </div>
  )
}

export default Login